import { Tab, TabGroup, TabList, TabPanel, TabPanels } from "@headlessui/react";
import { twMerge } from "tailwind-merge";

export type TabData = {
  key: string;
  label: React.ReactNode;
  content: React.ReactNode;
  disabled?: boolean;
  unmount?: boolean;
};

type Props = {
  tabs: TabData[];
  panelClassName?: string;
  panelsClassName?: string;
  tabClassName?: string;
  tabListClassName?: string;
  customButton?: React.ReactNode;
  onChange?: (key: string) => void;
  containerClassName?: string;
  /** Global unmount behavior for all tabs. Prefer tab-specific unmount prop over this. */
  unmountPanels?: boolean;
} & (
  | {
      // uncontrolled component
      selectedKey?: never;
      defaultActiveKey?: string;
    }
  | {
      // controlled component
      selectedKey?: string;
      defaultActiveKey?: never;
    }
);

export const Tabs: React.FC<Props> = ({
  tabs,
  panelClassName,
  panelsClassName,
  containerClassName,
  tabClassName = "px-3",
  tabListClassName,
  customButton,
  defaultActiveKey,
  selectedKey,
  unmountPanels = true,
  onChange,
}) => {
  const handleTabChange = (key: string) => {
    if (onChange) {
      onChange(key);
    }
  };

  const selectedIndex = tabs.findIndex((tab) => tab.key === selectedKey);
  const defaultActiveIndex = tabs.findIndex(
    (tab) => tab.key === defaultActiveKey,
  );

  const tabElements = tabs.map((tab) => (
    <Tab
      key={tab.key}
      className={({ selected }) =>
        twMerge(
          "relative py-2 font-inter-semibold-13px text-gray-800 focus:outline-none",
          selected &&
            "after:absolute after:right-0 after:bottom-0 after:left-0 after:block after:h-1 after:translate-y-1/2 after:rounded-full after:bg-indigo-500",
          tab.disabled && "opacity-60",
          tabClassName,
        )
      }
      data-loc={tab.key}
      disabled={tab.disabled}
    >
      {tab.label}
    </Tab>
  ));

  return (
    <TabGroup
      as="div"
      className={containerClassName}
      defaultIndex={defaultActiveIndex !== -1 ? defaultActiveIndex : undefined}
      selectedIndex={selectedIndex === -1 ? undefined : selectedIndex}
      onChange={(index) => handleTabChange(tabs[index].key)}
    >
      {customButton ? (
        <TabList className={twMerge("flex", tabListClassName)}>
          <div className="scrollbar-hide flex flex-1 overflow-x-auto overflow-y-hidden">
            {tabElements}
          </div>
          <div className="flex-shrink-0">{customButton}</div>
        </TabList>
      ) : (
        <TabList
          className={twMerge(
            "scrollbar-hide flex overflow-x-auto overflow-y-hidden",
            tabListClassName,
          )}
        >
          {tabElements}
        </TabList>
      )}
      <TabPanels className={panelsClassName}>
        {({ selectedIndex }) => (
          <>
            {tabs.map((tab, i) => (
              <TabPanel
                key={tab.key}
                as="div"
                className={panelClassName}
                data-loc={`${tab.key}-panel`}
                hidden={selectedIndex !== i}
                unmount={tab.unmount ?? unmountPanels}
              >
                {tab.content}
              </TabPanel>
            ))}
          </>
        )}
      </TabPanels>
    </TabGroup>
  );
};
